import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getApiUrl } from "./api";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    try {
      const json = JSON.parse(text);
      if (json.message) {
        const err = new Error(json.message) as Error & { status?: number; payload?: any };
        err.status = res.status;
        err.payload = json;
        throw err;
      }
    } catch (e) {
      if (e instanceof Error && e.message !== text) throw e; // Re-throw intentional errors
      // Not JSON or parse error, fall through
    }
    const fallbackErr = new Error(`${res.status}: ${text}`) as Error & { status?: number };
    fallbackErr.status = res.status;
    throw fallbackErr; // Fallback
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { timeoutMs?: number },
): Promise<Response> {
  const apiUrl = getApiUrl(url);
  console.log(`[QueryClient] Requesting: ${method} ${apiUrl}`);
  console.log(`[QueryClient] Full URL: ${apiUrl}`);
  
  // Get session token from localStorage
  const sessionToken = localStorage.getItem('sessionToken');
  const headers: Record<string, string> = {};
  
  if (data) {
    headers["Content-Type"] = "application/json";
  }
  
  // Add Authorization header if token exists
  if (sessionToken) {
    headers["Authorization"] = `Bearer ${sessionToken}`;
  }
  
  const timeoutMs = options?.timeoutMs ?? 30000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(apiUrl, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include", // Still try cookies as fallback
      signal: controller.signal,
    });

    await throwIfResNotOk(res);
    return res;
  } catch (err: any) {
    if (err?.name === "AbortError") {
      console.error(`❌ [QueryClient] Request Timeout (${timeoutMs}ms):`, `${method} ${apiUrl}`);
      throw new Error("Request Timeout");
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // 🛡️ CRITICAL: Respect explicit staleTime if provided in useQuery options
    // (This is handled by React Query internally, but we log for debugging)
    
    // Check if offline
    if (!navigator.onLine) {
       console.warn('⚠️ [QueryClient] Offline Mode Detected - Returning Null for Auth Check');
       if (queryKey[0] === '/api/auth/user') return null;
    }

    const apiUrl = getApiUrl(queryKey.join("/") as string);
    console.log(`[QueryClient] Fetching: ${apiUrl}`);
    
    // Get session token from localStorage
    const sessionToken = localStorage.getItem('sessionToken');
    const headers: Record<string, string> = {};
    
    // Add Authorization header if token exists
    if (sessionToken) {
      headers["Authorization"] = `Bearer ${sessionToken}`;
    }
    
    // 🛡️ TIMEOUT PROTECTION: Force 30s timeout to prevent infinite hang (Increased for Render Cold Start)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    
    let res;
    try {
        res = await fetch(apiUrl, {
          headers,
          credentials: "include", // Still try cookies as fallback
          signal: controller.signal
        });
        clearTimeout(timeoutId);
    } catch (err: any) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
            console.error('❌ [QueryClient] Force Timeout (30s) on:', apiUrl);
            throw new Error('Request Timeout');
        }
        throw err;
    }

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      console.log('🛑 [QueryClient] 401 Unauthorized - Returning Null');
      return null;
    }

    if (res.status === 401) {
       console.log('🛑 [QueryClient] 401 Unauthorized - Returning NULL (Explicit)');
       // IMPORTANT: Return null for user query so useQuery settles with data=null instead of error
       if (queryKey[0] === '/api/auth/user') return null;
       
       throw new Error("401 Unauthorized");
    }

    try {
      if (!res.ok) {
         await throwIfResNotOk(res);
      }
      console.log('✅ [QueryClient] Success:', apiUrl);
      return await res.json();
    } catch(e) {
      console.error('❌ [QueryClient] Error fetching:', e);
      throw e;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
